/* eslint-disable consistent-return */
/* eslint-disable complexity */
import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import Cookies from "js-cookie";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { v4 } from "uuid";
import * as EmailValidator from "email-validator";
import { TUCKERTUB_AUTH_TOKEN } from "~/constants";
import { useGetCartQuery, useLoginUserMutation } from "~/graphql/__generated__";
import { ButtonWithArrow, Title } from "~/ui";
import type { LoginSchemaInput } from "~/validation";
import { loginSchema } from "~/validation";
import { useGlobalState } from "~/context";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

export const SignIn = () => {
  const { openFullScreenModal, closeFullScreenModal } = useGlobalState();
  const [login, { loading: loginLoading }] = useLoginUserMutation();
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const [verEmail, setVerEmail] = useState("");
  const [verEmailError, setVerEmailError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isPasswordShow, setPasswordShow] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [result, setResult] = useState(null);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [emailBlurred, setEmailBlurred] = useState(false);
  const { data: cartData, refetch, loading: isCartLoading } = useGetCartQuery();

  const router = useRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, touchedFields, dirtyFields },
    setValue,
    reset,
  } = useForm<LoginSchemaInput>({
    resolver: zodResolver(loginSchema),
    mode: "all",
  });

  const containsErrorsEmail = Object.keys(errors).includes("email");
  const containsErrorsPassword = Object.keys(errors).includes("password");

  const emailValidation =
    !containsErrorsEmail && dirtyFields.email
      ? "primary-input-isValid"
      : touchedFields.email && "primary-input-isInvalid";
  const passwordValidation =
    !containsErrorsPassword && dirtyFields.password
      ? "primary-input-isValid"
      : touchedFields.password && "primary-input-isInvalid";

  useEffect(() => {
    if (
      validationResult?.message === "Domain does not exist." ||
      validationResult?.message === "Internal server error."
    ) {
      //alert(JSON.stringify(validationResult))
      setIsValidEmail(false);
    }
    //alert(validationResult?.message)
  }, [validationResult]);

  const onSubmitForm = async (values: LoginSchemaInput) => {
    setVerEmail(values.email)
    openFullScreenModal();
    try {
      if (authErrorMessage) {
        setAuthErrorMessage("");
      }
      const { data, errors } = await login({
        variables: {
          input: {
            clientMutationId: v4(),
            username: values.email,
            password: values.password,
          },
        },
      });

      if (errors) {
        closeFullScreenModal();
        throw new Error(errors[0].message);
      }

      reset();
      Cookies.set(TUCKERTUB_AUTH_TOKEN, data?.login?.authToken || "no-token");
      setSuccessMessage("Successfully signed in!");

      refetch()

      closeFullScreenModal();
      router.replace(
        `${process.env.NEXT_PUBLIC_FRONTEND_URL}/account/dashboard`
      );

      // const responseOptions = {
      //   method: 'POST',
      //   body: JSON.stringify(values),
      //   headers: {
      //     'Content-Type': 'application/json'
      //   }
      // }
      // const response = await fetch(
      //   `${process.env.NEXT_PUBLIC_FRONTEND_URL}/api/auth/get-jwt-token`,
      //   responseOptions
      // )
      // const data = await response.json()

      // if (data.success) {
      //   // toast.success('Signed in!')
      //   login(data.data.jwt)
      //   reset()
      //   setSuccessMessage('Successfully signed in!')
      //   router.replace(`${process.env.NEXT_PUBLIC_FRONTEND_URL}/account/dashboard`)
      // } else {
      //   throw new Error(data.data?.message)
      // }
    } catch (error: any) {
      // console.log("error", error?.message);

      closeFullScreenModal();

      if (error?.message?.includes("uv_authentication_failed")) {
        return setAuthErrorMessage("Please verify your email address first!");
      } else if (error instanceof Error) {
        return setAuthErrorMessage("Invalid username or password");

        // toast.error(error.message)
      }

      setAuthErrorMessage("Something went wrong.");
    }
    closeFullScreenModal();
  };

  async function handleResendEmailVerificationmail() {
    //alert("called")
    try {
      // if (authErrorMessage) {
      //   setAuthErrorMessage('');
      // }
      openFullScreenModal()
      const responseOptions = {
        method: 'POST',
        body: JSON.stringify({

          email: verEmail
        }),
        headers: {
          'Content-Type': 'application/json'
        }
      };
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_FRONTEND_URL}/api/account/resend-email-verification`,
        responseOptions
      );
      const data = await response.json();
      //console.log("Email verification data: " , data)

      if (data.status) {
        closeFullScreenModal()
        // toast.success('Registered!')
        //reset();
        setSuccessMessage(data?.message);
        setAuthErrorMessage('');
        //router.push('/account');
      } else {
        throw new Error(data.errors?.[0].message);
      }
    } catch (error) {
      closeFullScreenModal()
      setSuccessMessage('');
      // if (error instanceof Error) {
      //   if (error.message.includes('Password reset link is invalid')) {
      //     return setAuthErrorMessage(error.message);
      //   }
      //   // toast.error(error.message)
      // }
      setAuthErrorMessage('Something went wrong');
    }
    closeFullScreenModal()

  }

  function getRestPartAfterFirstDot(domain: any) {
    const parts = domain.split(".");
    return parts.slice(1).join(".");
  }
  const verifyTLD = async (email1: any) => {
    try {
      const domain1 = email1.split("@")[1];
      // const domain2 = domain1.split(".")[2];
      const domain2 = getRestPartAfterFirstDot(domain1);
      const response = await fetch(`/api/mandrill/verify-email?tld=${domain2}`);
      const data = await response.json();
      setResult(data.exists);
    } catch (error) {
      // console.error("Failed to verify TLD:", error);
    }
  };

  const handleValidateEmail = async (domain: any) => {
    try {
      const domain1 = domain.split("@")[1];
      const response = await fetch("/api/mandrill/verify-email-deep", {
        //const response = await fetch("/api/mandrill/verify-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ domain: domain1 }),
      });

      if (response.ok) {
        const data = await response.json();
        setValidationResult(data);
      } else {
        setValidationResult(null);
      }
    } catch (error) {
      // console.error("Error:", error);
      setValidationResult(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;

    setIsValidEmail(EmailValidator.validate(fieldValue));
    handleValidateEmail(fieldValue);
    setValue(fieldName as any, fieldValue as any);
    // setEmail(fieldValue);
    verifyTLD(fieldValue);
  };
  const handleInputBlur = () => {
    setEmailBlurred(true);
  };


  return (
    <section className="max-w-[440px]">
      <Title className="text-colorFourteen">Sign In</Title>
      <p className="py-8 text-base text-colorFourteen">
        Enter your email and password to access your account
      </p>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <input
              type="email"
              name="email"
              // style={{
              //   boxShadow: "none",
              //   borderColor: "transparent",
              // }}
              className={`primary-input input-border ${emailValidation}`}
              placeholder="Email address"
              autoComplete="email"
              aria-label="Email address"
              //{...register("email")}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
            />
            {/* <p className="p-0 text-colorThirteen">
              {emailValidation && errors.email?.message}
            </p> */}
            {(validationResult?.valid && isValidEmail) ||
              (result && isValidEmail) ? (
              <p className="p-0 text-colorThirteen">{containsErrorsEmail && errors.email?.message}</p>
            ) : (
              emailBlurred === true && <p className="p-0 text-colorThirteen">
                Please enter a valid email address.
              </p>
            )}
          </div>
          <div>
            <div
              className="register-box-alignment"
            // style={{
            //   display: "flex",
            //   alignItems: "flex-end",
            //   flexDirection: "column",
            // }}
            >
              <input
                type={isPasswordShow ? "text" : "password"}
                // style={{
                //   boxShadow: "none",
                //   borderColor: "transparent",
                // }}
                className={`primary-input input-border ${passwordValidation}`}
                placeholder="Password"
                autoComplete="current-password"
                aria-label="Password"
                {...register("password")}
              />
              <span
                onClick={() => setPasswordShow((e) => !e)}
                className="register-showpass-alignment"
              // style={{
              //   position: "relative",
              //   right: 10,
              //   top: -30,
              //   cursor: "pointer",
              // }}
              >
                {" "}
                {isPasswordShow ? (
                  <AiOutlineEye />
                ) : (
                  <AiOutlineEyeInvisible />
                )}{" "}
              </span>
            </div>
            <p
              // style={{ marginTop: -20 }}
              className="p-0 text-colorThirteen signin-pass-err"
            >
              {passwordValidation && errors.password?.message}
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2">
            <ButtonWithArrow
              buttonType="submit"
              buttonVariant="fourth"
              className="w-[240px] flex-1 justify-between rounded-lg text-lg"
              disabled={
                isSubmitting ||

                !result ||
                result === null ||
                !isValidEmail
              }
            >
              {loginLoading || isSubmitting ? "Signing In..." : "Sign In"}
            </ButtonWithArrow>
            <NextLink
              target="_blank"
              href="/forgot-password"
              className="flex flex-1 items-center justify-end text-sm underline underline-offset-4"
            >
              Forgot Password
            </NextLink>
          </div>
          {authErrorMessage && !successMessage && (
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2">
              <p className="p-0 text-colorThirteen">{authErrorMessage}</p>
              {authErrorMessage === "Please verify your email address first!" && <NextLink
                //target="_blank"
                onClick={handleResendEmailVerificationmail}
                href="#"
                className="flex flex-1 items-center justify-end text-sm underline underline-offset-4"
              >
                Resend Verification Email
              </NextLink>}
            </div>
          )}
          {successMessage && !authErrorMessage && (
            <div>
              <p className="p-0 text-colorTen">{successMessage}</p>
            </div>
          )}
        </div>
      </form>
    </section>
  );
};
